import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { Suggestion } from '@tiptap/suggestion'
import argsSuggestion from './argsSuggestion'
import slashSuggestion from './slashSuggestion'
import SlashCommandNode from './SlashCommandNode'
import { setActiveArgs } from './suggestionState'
import type { TComposerContext } from './composerContext'

export type TSlashCommandAttrs = {
  command: string
  pubkey: string
  d: string
  author?: string
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    slashCommand: {
      insertSlashCommand: (attrs: TSlashCommandAttrs) => ReturnType
    }
  }
}

/**
 * Renders command and its author reference as one atom. Serializes to the
 * command invocation syntax used in event content.
 */
const TTSlash = Node.create({
  name: 'slashCommand',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: false,

  addOptions() {
    return { HTMLAttributes: {} }
  },

  addStorage() {
    return { context: null as TComposerContext | null }
  },

  addNodeView() {
    return ReactNodeViewRenderer(SlashCommandNode)
  },

  addAttributes() {
    return {
      command: { default: null },
      pubkey: { default: null },
      d: { default: null },
      npub: { default: null }
    }
  },

  parseHTML() {
    return [{ tag: 'span[data-slash-command]' }]
  },

  renderText({ node }) {
    return `/${node.attrs.command}${node.attrs.npub ? `@nostr:${node.attrs.npub}` : ''}`
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-slash-command': ''
      }),
      `/${node.attrs.command}${node.attrs.npub ? `@nostr:${node.attrs.npub}` : ''}`
    ]
  },

  addCommands() {
    return {
      insertSlashCommand:
        (attrs: TSlashCommandAttrs) =>
        ({ commands }) => {
          return commands.insertContent({ type: this.name, attrs })
        }
    }
  },

  addProseMirrorPlugins() {
    const { editor } = this
    return [
      Suggestion({
        editor,
        ...slashSuggestion
      }),
      Suggestion({
        editor,
        ...argsSuggestion
      })
    ]
  }
})

export { setActiveArgs }
export type { TComposerContext }
export default TTSlash
